import React, { useEffect, useState } from 'react';

import {
  listCoWorkers,
  removeCoWorker,
} from '../../services/api/visitCoworkerApi';
import eventBus from '../../services/realtime/EventBus';
import { REALTIME_EVENTS } from '../../services/realtime/RealtimeSyncService';

/**
 * CoWorkerAvatarsRow — Phase 6.0 / 10.
 *
 * Renders the assigned doctor + co-workers as compact pills on the
 * visit detail surface. The "remove" affordance is visible only to
 * the assigned doctor / ADMIN.
 *
 * Props:
 *   visit          — visit row (carries `doctor_member_id`, `doctor_name`).
 *   viewer         — { memberId, role }
 *   onAddClick     — () => void   opens CoWorkerPickerDialog from parent.
 */
function _initials(name) {
  if (!name) return '??';
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase();
}

export default function CoWorkerAvatarsRow({ visit, viewer, onAddClick }) {
  const [coworkers, setCoworkers] = useState([]);
  const [loading, setLoading] = useState(false);

  const isAssignedDoctor = visit?.doctor_member_id === viewer?.memberId;
  const isAdmin = (viewer?.role || '').toUpperCase() === 'ADMIN';
  const canManage = isAssignedDoctor || isAdmin;

  useEffect(() => {
    if (!visit?.visit_id) return undefined;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await listCoWorkers(visit.visit_id);
        if (!cancelled) setCoworkers(res?.co_workers || []);
      } catch {
        if (!cancelled) setCoworkers([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();

    const onChanged = (payload) => {
      if (payload?.visitId === visit.visit_id) load();
    };
    const offAdd = eventBus.on(REALTIME_EVENTS.VISIT_COWORKER_ADDED, onChanged);
    const offRemove = eventBus.on(
      REALTIME_EVENTS.VISIT_COWORKER_REMOVED, onChanged,
    );
    return () => { cancelled = true; offAdd?.(); offRemove?.(); };
  }, [visit?.visit_id]);

  const onRemove = async (memberId) => {
    try {
      await removeCoWorker(visit.visit_id, memberId);
      setCoworkers((prev) => prev.filter((c) => c.member_id !== memberId));
    } catch (err) {
      // best-effort; reload on event
    }
  };

  const assignedDoctorPill = visit?.doctor_member_id ? (
    <span
      key="assigned"
      className="inline-flex items-center gap-1.5 rounded-full border border-indigo-400 dark:border-indigo-700 bg-indigo-100 dark:bg-indigo-900/40 text-indigo-900 dark:text-indigo-100 px-2 py-1 text-xs font-medium"
      title={`Assigned doctor: ${visit.doctor_name || visit.doctor_member_id}`}
    >
      <span className="rounded-full bg-indigo-300 dark:bg-indigo-700 text-indigo-900 dark:text-indigo-100 w-5 h-5 flex items-center justify-center text-[10px] font-bold">
        {_initials(visit.doctor_name)}
      </span>
      <span>{visit.doctor_name || visit.doctor_member_id}</span>
      <span className="text-[10px] opacity-70">primary</span>
    </span>
  ) : null;

  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {assignedDoctorPill}
      {coworkers.map((c) => (
        <span
          key={c.member_id}
          className={[
            'inline-flex items-center gap-1.5 rounded-full border px-2 py-1 text-xs font-medium',
            c.is_active
              ? 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100'
              : 'border-slate-300 dark:border-slate-600 bg-slate-100 dark:bg-slate-800/40 text-slate-400 dark:text-slate-500 line-through',
          ].join(' ')}
          title={`Co-worker: ${c.name || c.member_id}${c.is_active ? '' : ' (inactive)'}`}
        >
          <span className="rounded-full bg-slate-300 dark:bg-slate-600 text-slate-800 dark:text-slate-100 w-5 h-5 flex items-center justify-center text-[10px] font-bold">
            {_initials(c.name)}
          </span>
          <span>{c.name || c.member_id}</span>
          {canManage && (
            <button
              type="button"
              onClick={() => onRemove(c.member_id)}
              className="ml-1 text-slate-500 hover:text-rose-600 dark:hover:text-rose-300"
              title="Remove co-worker"
              aria-label={`Remove ${c.name}`}
            >
              ✗
            </button>
          )}
        </span>
      ))}
      {canManage && (
        <button
          type="button"
          onClick={onAddClick}
          className="inline-flex items-center gap-1 rounded-full border border-dashed border-slate-400 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 px-2 py-1 text-xs font-medium hover:bg-slate-50 dark:hover:bg-slate-700"
        >
          + Add co-worker
        </button>
      )}
      {loading && coworkers.length === 0 && (
        <span className="text-xs text-slate-400">…</span>
      )}
    </div>
  );
}
